import { useMutation, useQuery } from "@apollo/client";
import { Modal } from "@mantine/core";
import { useAppSelector } from "app/hooks";
import { MessageType } from "components/forms/message-view";
import { useFormik } from "formik";
import { markMessageRead, postClaimMessage } from "graphql/mutations/claims";
import { getClaimThread } from "graphql/queries/claims";
import { GetClaimsQuery } from "graphql/queries/__generated__/claims";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

type ClaimData = NonNullable<NonNullable<GetClaimsQuery["getClaims"]>[number]>;

interface MessageFormValues {
    message: string;
}

interface ClaimThreadContainerProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    claim: ClaimData;
    refetch?: () => void;
}

const ClaimThreadContainer: FC<ClaimThreadContainerProps> = ({
    open,
    setOpen,
    claim,
    refetch,
}) => {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<MessageType[]>([]);
    const hasMarkedAsRead = useRef(false);
    const { user } = useAppSelector((state) => state.auth);

    const reinsurerId =
        user?.clientable?.__typename === "Reinsurer_representative"
            ? Number(user.clientable.reinsurer?.reinsurer_id)
            : undefined;

    const {
        data,
        loading: fetchLoading,
        refetch: refetchMessages,
    } = useQuery(getClaimThread, {
        variables: {
            claimId: claim.offer_claim_id,
            reinsurerId: reinsurerId,
        },
        skip: !open,
        fetchPolicy: "network-only",
    });

    const [sendMessage, { loading: sendLoading }] =
        useMutation(postClaimMessage);
    const [markAsRead] = useMutation(markMessageRead);

    useEffect(() => {
        if (data?.getClaimThread?.data?.messages) {
            const formattedMessages: MessageType[] =
                data.getClaimThread.data.messages.map((msg: any) => ({
                    uuid: msg.id,
                    message: msg.body,
                    timestamp: msg.created_at,
                    sender_name: msg.sender_name,
                    state: msg.is_internal ? "INTERNAL" : "EXTERNAL",
                }));
            setMessages(formattedMessages);

            const unreadMessages = data.getClaimThread.data.messages
                .filter((msg: any) => !msg.is_read)
                .map((msg: any) => msg.id);

            if (unreadMessages.length > 0) {
                markUnreadMessagesAsRead(unreadMessages);
            }
        }
    }, [data]);

    const markUnreadMessagesAsRead = (messageIds: string[]) => {
        if (!open || messageIds.length === 0) return;

        messageIds.forEach((messageId) => {
            markAsRead({
                variables: {
                    messageId,
                },
            })
                .then(() => {
                    hasMarkedAsRead.current = true;
                })
                .catch((error) => {
                    console.error("Error marking message as read:", error);
                });
        });
    };

    const handleMessageSubmission = async (message: string) => {
        if (!reinsurerId) {
            console.error("Reinsurer ID is required to send a message");
            return;
        }

        return await sendMessage({
            variables: {
                claimId: claim.offer_claim_id,
                reinsurerId: reinsurerId,
                body: message,
                isInternal: true,
            },
        });
    };

    const form = useFormik<MessageFormValues>({
        initialValues: {
            message: "",
        },
        onSubmit: async (values, { resetForm }) => {
            const plainTextMessage = values.message
                .replace(/<[^>]*>/g, "")
                .trim();
            if (!plainTextMessage) return;

            try {
                const newMessage: MessageType = {
                    uuid: `temp-${Date.now()}`,
                    message: values.message,
                    timestamp: new Date().toISOString(),
                    sender_name: "You",
                    state: "INTERNAL",
                };

                setMessages((prev) => [...prev, newMessage]);

                await handleMessageSubmission(values.message);

                resetForm();
                refetchMessages();
                hasMarkedAsRead.current = true;
            } catch (error) {
                console.error("Error sending message:", error);
            }
        },
    });

    const quillModules = useMemo(
        () => ({
            toolbar: [
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["clean"],
            ],
        }),
        [],
    );

    const quillFormats = useMemo(
        () => ["bold", "italic", "underline", "strike", "list", "bullet"],
        [],
    );

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const formatCurrency = (
        amount: number | null | undefined,
        currency: string | null | undefined,
    ) => {
        if (!amount || !currency) return "N/A";
        return `${currency} ${amount.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const isLoading = sendLoading;

    const handleModalClose = () => {
        setOpen(false);
        if (hasMarkedAsRead.current && refetch) {
            refetch();
            hasMarkedAsRead.current = false;
        }
    };

    return (
        <Modal
            opened={open}
            onClose={handleModalClose}
            title={t("modal_titles.claim_discussion")}
            size="xl">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <svg
                            className="h-6 w-6 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                            />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h4 className="text-sm font-semibold text-blue-900 mb-2">
                            {t("claim_thread.secure_title")}
                        </h4>
                        <p className="text-sm text-blue-800 leading-relaxed">
                            {t("claim_thread.secure_description")}
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-md mb-4 border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">
                    {t("claim_thread.claim_details")}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                    <p className="text-sm">
                        <span className="text-gray-500">{t("claim_thread.claim_id")}:</span>{" "}
                        <span className="font-medium text-gray-800">
                            {claim.offer_claim_id || t("common.na")}
                        </span>
                    </p>
                    <p className="text-sm">
                        <span className="text-gray-500">{t("claim_thread.claim_amount")}:</span>{" "}
                        <span className="font-medium text-gray-800">
                            {formatCurrency(claim.claim_amount, claim.currency)}
                        </span>
                    </p>
                    <p className="text-sm">
                        <span className="text-gray-500">{t("claim_thread.claim_date")}:</span>{" "}
                        <span className="font-medium text-gray-800">
                            {formatDate(claim.claim_date)}
                        </span>
                    </p>
                    <p className="text-sm">
                        <span className="text-gray-500">{t("claim_thread.rate")}:</span>{" "}
                        <span className="font-medium text-gray-800">
                            {claim.rate}%
                        </span>
                    </p>
                    <p className="text-sm">
                        <span className="text-gray-500">{t("claim_thread.insured_by")}:</span>{" "}
                        <span className="font-medium text-gray-800">
                            {claim.offer?.offer_detail?.insured_by || t("common.na")}
                        </span>
                    </p>
                    <p className="text-sm">
                        <span className="text-gray-500">{t("claim_thread.policy_number")}:</span>{" "}
                        <span className="font-medium text-gray-800">
                            {claim.offer?.offer_detail?.policy_number || t("common.na")}
                        </span>
                    </p>
                </div>
            </div>

            <div className="mt-2">
                <p className="text-sm text-gray-500">
                    {t("claim_thread.communication_thread")}
                </p>
            </div>

            <div className="mt-5 mb-5">
                {fetchLoading ? (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {messages.length === 0 ? (
                            <div className="text-center py-10">
                                <p className="text-gray-500">
                                    {t("claim_thread.no_messages")}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-96 overflow-y-auto px-1">
                                {messages.map((message) => (
                                    <div
                                        key={message.uuid}
                                        className={`p-3 rounded-lg ${
                                            message.state === "INTERNAL"
                                                ? "bg-blue-50 border border-blue-100 ml-6"
                                                : "bg-gray-50 border border-gray-100 mr-6"
                                        }`}>
                                        <div className="flex justify-between items-start mb-1">
                                            <span className="font-medium text-sm">
                                                {message.sender_name
                                                    .split(" ")
                                                    .map(
                                                        (word) =>
                                                            word
                                                                .charAt(0)
                                                                .toUpperCase() +
                                                            word
                                                                .slice(1)
                                                                .toLowerCase(),
                                                    )
                                                    .join(" ")}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {new Date(
                                                    message.timestamp,
                                                ).toLocaleString()}
                                            </span>
                                        </div>
                                        <div
                                            className="prose prose-sm max-w-none"
                                            dangerouslySetInnerHTML={{
                                                __html: message.message,
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={form.handleSubmit} className="mt-4">
                            <div className="rounded-md border border-gray-300 overflow-hidden">
                                <style>{`
                  .ql-editor {
                    min-height: 80px !important;
                  }
                `}</style>
                                <ReactQuill
                                    value={form.values.message}
                                    onChange={(value) =>
                                        form.setFieldValue("message", value)
                                    }
                                    modules={quillModules}
                                    formats={quillFormats}
                                    placeholder={t("messaging.type_message")}
                                    style={{
                                        backgroundColor: "white",
                                    }}
                                />
                            </div>
                        </form>
                    </div>
                )}
            </div>

            <div className="mt-6 flex justify-end space-x-2">
                <button
                    type="button"
                    onClick={form.submitForm}
                    disabled={
                        !form.values.message.replace(/<[^>]*>/g, "").trim() ||
                        isLoading
                    }
                    className="inline-flex items-center rounded-md border mx-1 border-transparent text-sm font-medium px-3 py-1.5 text-white shadow-sm bg-green-700 hover:bg-green-800 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed">
                    {isLoading ? (
                        <>
                            <span className="mr-2 animate-spin h-4 w-4 border-t-2 border-r-2 border-white rounded-full"></span>
                            {t("messaging.sending")}
                        </>
                    ) : (
                        t("messaging.send_message")
                    )}
                </button>
            </div>
        </Modal>
    );
};

export default ClaimThreadContainer;
